import { Link } from "react-router-dom";
import Button from "./ui/Button.jsx";
import Badge from "./ui/Badge.jsx";
import FRAMEWORKS from "../compliance/frameworks.js";
import { formatComplianceScopeDisplay } from "../compliance/complianceScopeDisplay.js";
import { getDefaultProviderComplianceFrameworks } from "../utils/providerFrameworks.js";
import { getProviderMeta, getProviderMetaForProject, resolveProviderId } from "../utils/providerContext.js";
import { extractOryPostureGaps } from "../utils/oryPostureGaps.js";
import OryCompletenessReport from "./ory/OryCompletenessReport.jsx";
import ThreatContextBlock from "./ThreatContextBlock.jsx";
import FindingRemediationBlock from "./remediation/FindingRemediationBlock.jsx";
import AutomationProgressBar from "./command/AutomationProgressBar.jsx";
import SnapshotPicker from "./workspace/SnapshotPicker.jsx";

export default function FindingsBrief({
  findings,
  summary,
  onAction,
  snapshots = [],
  selectedSnapshotId = null,
  onSelectSnapshot = null,
  onRunNewAssessment = null,
  snapshotsBusy = false,
  snapshotsError = "",
  assessmentProgressSteps = null,
  projectId = null,
  reportSnapshotId = null,
  providerId = "okta",
  normalizedFindings = null,
  isGreenfield = false,
  /** When true, snapshot/run controls render in WorkspaceCommandBar instead. */
  hideWorkspaceControls = false,
  project = null,
}) {
  const resolvedProvider = resolveProviderId(project || { providerId }) || "okta";
  const providerMeta = project ? getProviderMetaForProject(project) : getProviderMeta(resolvedProvider);
  const isOry = resolvedProvider === "ory";
  const postureGaps = isOry ? extractOryPostureGaps(normalizedFindings) : { absent: [], deferred: [], total: 0 };
  const defaultFrameworks = getDefaultProviderComplianceFrameworks(resolvedProvider);
  const crit = Array.isArray(findings?.critical) ? findings.critical : [];
  const warn = Array.isArray(findings?.warnings) ? findings.warnings : [];
  const healthy = Array.isArray(findings?.healthy) ? findings.healthy : [];
  const inc = Array.isArray(findings?.inconclusive) ? findings.inconclusive : [];
  const incAccessGap = inc.filter((f) => f?.inconclusiveCause === "access_gap");
  const incDataAbsent = inc.filter(
    (f) => f?.inconclusiveCause === "data_absent" || !f?.inconclusiveCause,
  );
  const other = Array.isArray(findings?.other) ? findings.other : [];
  const totalUsers = summary?.totalUsers ?? "—";
  const totalApps = summary?.totalApps ?? "—";
  const selected = summary?.compliance?.selectedFrameworks || defaultFrameworks;
  const effective = summary?.compliance?.effectiveFrameworks || selected;

  const reportTo =
    projectId &&
    `/project/${encodeURIComponent(String(projectId))}/report${
      reportSnapshotId ? `/${encodeURIComponent(String(reportSnapshotId))}` : ""
    }`;

  const leftFindingsPresent =
    crit.length > 0 || warn.length > 0 || incAccessGap.length > 0 || incDataAbsent.length > 0 || other.length > 0;
  const oryCompleteness = summary?.oryCompleteness || null;
  const showOryCompleteness = isOry && oryCompleteness;
  const showPostureGaps = isOry && postureGaps.total > 0;

  const reportLinkStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    borderRadius: "4px",
    border: "1px solid var(--border-bright)",
    background: "var(--bg-elevated)",
    color: "var(--text-primary)",
    padding: "6px 12px",
    fontSize: "12px",
    minHeight: "32px",
    textDecoration: "none",
  };

  return (
    <div
      style={{
        borderBottom: hideWorkspaceControls ? "none" : "1px solid var(--border)",
        paddingBottom: hideWorkspaceControls ? "0" : "24px",
        marginBottom: hideWorkspaceControls ? "0" : "20px",
      }}
    >
      <header style={{ marginBottom: "16px" }}>
        <h2 style={{ fontSize: "18px", fontWeight: 600, marginBottom: "4px" }}>
          {snapshotsBusy
            ? "Running org analysis…"
            : isOry && isGreenfield
              ? "Fresh Ory project — baseline posture"
              : `Org analysis complete — ${totalUsers} users, ${totalApps} apps`}
        </h2>
        {isOry && isGreenfield && !snapshotsBusy ? (
          <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.45, marginBottom: "8px" }}>
            {providerMeta?.greenfieldFindingsIntro ||
              "Fresh tenant baseline — review posture gaps below, then use discovery to plan next steps."}
          </p>
        ) : null}
        {snapshotsBusy && !hideWorkspaceControls && Array.isArray(assessmentProgressSteps) && assessmentProgressSteps.length > 0 ? (
          <AutomationProgressBar steps={assessmentProgressSteps} />
        ) : null}
        {!hideWorkspaceControls ? (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <SnapshotPicker
            snapshots={snapshots}
            selectedSnapshotId={selectedSnapshotId}
            onSelectSnapshot={onSelectSnapshot}
            busy={snapshotsBusy}
          />
          <button
            type="button"
            onClick={() => onRunNewAssessment?.()}
            disabled={snapshotsBusy}
            className="rounded border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] font-medium text-slate-200 hover:bg-white/[0.08] disabled:opacity-40"
          >
            Run new assessment
          </button>
          {snapshotsError ? (
            <span className="text-dim" style={{ fontSize: "11px" }}>
              {snapshotsError}
            </span>
          ) : null}
        </div>
        ) : null}
      </header>

      <CompliancePosture
        findings={findings}
        selectedFrameworks={selected}
        effectiveFrameworks={effective}
        providerId={resolvedProvider}
        onClickFramework={(id) => onAction?.(`framework:${id}`)}
      />

      {/* lg: = min-width 1024px — wide: 2fr / 1fr columns; narrow: single column same section order */}
      <div
        className={
          leftFindingsPresent
            ? "mb-5 grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:gap-5"
            : "mb-5 grid grid-cols-1 items-start gap-4"
        }
      >
        {leftFindingsPresent ? (
          <div className="flex min-w-0 flex-col gap-6">
          {crit.length > 0 ? (
            <section>
              <p style={{ fontSize: "11px", fontWeight: 600, marginBottom: "10px", color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--critical)", marginRight: "6px" }}>●</span>
                Needs attention
              </p>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "10px" }}>
                {crit.map((f) => (
                  <li
                    key={f.id || f.ruleId || f.title}
                    style={{
                      borderLeft: "3px solid var(--critical)",
                      padding: "10px 12px",
                      background: "var(--bg-elevated)",
                      borderRadius: "4px",
                    }}
                  >
                    <FindingHeading finding={f} />
                    <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.45 }}>
                      {f.detail}
                    </p>
                    <FindingRemediationBlock ruleId={f.ruleId} providerId={resolvedProvider} />
                    <Citations finding={f} effectiveFrameworks={effective} />
                    <ThreatContextBlock references={f.threatReferences} />
                    {f.autoFixable ? (
                      <div style={{ marginTop: "8px" }}>
                        <Badge variant="info">Auto-fixable</Badge>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {warn.length > 0 ? (
            <section>
              <p style={{ fontSize: "11px", fontWeight: 600, marginBottom: "10px", color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--warning)", marginRight: "6px" }}>●</span>
                Should address
              </p>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "10px" }}>
                {warn.map((f) => (
                  <li
                    key={f.id || f.ruleId || f.title}
                    style={{
                      borderLeft: "3px solid var(--warning)",
                      padding: "10px 12px",
                      background: "var(--bg-elevated)",
                      borderRadius: "4px",
                    }}
                  >
                    <FindingHeading finding={f} />
                    <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.45 }}>
                      {f.detail}
                    </p>
                    <FindingRemediationBlock ruleId={f.ruleId} providerId={resolvedProvider} />
                    <Citations finding={f} effectiveFrameworks={effective} />
                    <ThreatContextBlock references={f.threatReferences} />
                    {f.autoFixable ? (
                      <div style={{ marginTop: "8px" }}>
                        <Badge variant="info">Auto-fixable</Badge>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {incAccessGap.length > 0 ? (
            <section>
              <p style={{ fontSize: "11px", fontWeight: 600, marginBottom: "10px", color: "var(--text-secondary)" }}>
                <span style={{ color: "#94a3b8", marginRight: "6px" }} aria-hidden>
                  ⓘ
                </span>
                Couldn&apos;t assess — access / permission / license
              </p>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "10px" }}>
                {incAccessGap.map((f) => (
                  <li
                    key={f.id || f.ruleId || f.title}
                    style={{
                      borderLeft: "3px solid #64748b",
                      padding: "10px 12px",
                      background: "rgba(148, 163, 184, 0.12)",
                      borderRadius: "4px",
                    }}
                  >
                    <FindingHeading finding={f} />
                    <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.45 }}>
                      {f.detail}
                    </p>
                    {f.unblockHint ? (
                      <p className="text-dim" style={{ fontSize: "12px", lineHeight: 1.45, marginTop: "8px" }}>
                        <span style={{ fontStyle: "italic" }}>What would unblock:</span> {f.unblockHint}
                      </p>
                    ) : null}
                    <Citations finding={f} effectiveFrameworks={effective} />
                    <ThreatContextBlock references={f.threatReferences} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {incDataAbsent.length > 0 ? (
            <section>
              <p style={{ fontSize: "11px", fontWeight: 600, marginBottom: "10px", color: "var(--text-secondary)" }}>
                <span style={{ color: "#94a3b8", marginRight: "6px" }} aria-hidden>
                  ⓘ
                </span>
                Unable to evaluate
              </p>
              <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "10px" }}>
                {incDataAbsent.map((f) => (
                  <li
                    key={f.id || f.ruleId || f.title}
                    style={{
                      borderLeft: "3px solid #64748b",
                      padding: "10px 12px",
                      background: "rgba(148, 163, 184, 0.12)",
                      borderRadius: "4px",
                    }}
                  >
                    <FindingHeading finding={f} />
                    <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.45 }}>
                      {f.detail}
                    </p>
                    {f.unblockHint ? (
                      <p className="text-dim" style={{ fontSize: "12px", lineHeight: 1.45, marginTop: "8px" }}>
                        <span style={{ fontStyle: "italic" }}>What would unblock:</span> {f.unblockHint}
                      </p>
                    ) : null}
                    <Citations finding={f} effectiveFrameworks={effective} />
                    <ThreatContextBlock references={f.threatReferences} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {other.length > 0 ? (
            <section>
              <p style={{ fontSize: "11px", fontWeight: 600, marginBottom: "10px", color: "var(--text-secondary)" }}>
                <span style={{ color: "var(--text-dim)", marginRight: "6px" }}>●</span>
                Other findings (out of compliance scope)
              </p>
              <details style={{ background: "var(--bg-elevated)", borderRadius: "4px", padding: "10px 12px" }}>
                <summary className="text-secondary" style={{ cursor: "pointer", fontSize: "13px" }}>
                  Show {other.length} more
                </summary>
                <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "10px", marginTop: "10px" }}>
                  {other.map((f) => (
                    <li
                      key={f.id || f.ruleId || f.title}
                      style={{
                        borderLeft: "3px solid var(--border)",
                        padding: "10px 12px",
                        background: "var(--bg-surface)",
                        borderRadius: "4px",
                      }}
                    >
                      <FindingHeading finding={f} />
                      <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.45 }}>
                        {f.detail}
                      </p>
                      <FindingRemediationBlock ruleId={f.ruleId} providerId={resolvedProvider} />
                      <Citations finding={f} effectiveFrameworks={effective} />
                      <ThreatContextBlock references={f.threatReferences} />
                    </li>
                  ))}
                </ul>
              </details>
            </section>
          ) : null}
          </div>
        ) : null}

        <section className="min-w-0">
          <p style={{ fontSize: "11px", fontWeight: 600, marginBottom: "8px", color: "var(--text-secondary)" }}>
            <span style={{ color: "var(--healthy)", marginRight: "6px" }}>●</span>
            Looking good
          </p>
          {healthy.length === 0 ? (
            <p className="text-dim" style={{ fontSize: "12px", lineHeight: 1.4 }}>
              No healthy highlights yet — run a fuller audit to populate positives.
            </p>
          ) : (
            <ul className="list-none space-y-1 p-0 m-0" style={{ fontSize: "12px", lineHeight: 1.35 }}>
              {healthy.map((f) => (
                <li
                  key={f.id || f.ruleId || f.title}
                  className="flex min-w-0 items-baseline gap-1.5"
                  title={f.title}
                >
                  <span className="shrink-0 text-[11px]" style={{ color: "var(--healthy)" }} aria-hidden>
                    ✓
                  </span>
                  <span className="text-secondary min-w-0 truncate">{f.title}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {showOryCompleteness ? (
        <OryCompletenessReport completeness={oryCompleteness} normalizedFindings={normalizedFindings} />
      ) : null}

      {showPostureGaps ? (
        <PostureGapsPanel
          gaps={postureGaps}
          absentIntro={providerMeta?.postureGapAbsentIntro}
          deferredIntro={providerMeta?.postureGapDeferredIntro}
          effectiveFrameworks={effective}
        />
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
        {crit.length > 0 ? (
          <Button variant="primary" size="sm" onClick={() => onAction?.("fix-critical")}>
            Fix critical issues
          </Button>
        ) : null}
        {reportTo ? (
          <Link to={reportTo} style={reportLinkStyle}>
            Full audit report
          </Link>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => onAction?.("full-report")}>
            Full audit report
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => onAction?.("specific-task")}>
          I have something specific to do
        </Button>
      </div>
    </div>
  );
}

function FindingHeading({ finding }) {
  const ruleId = finding?.ruleId || finding?.rule_id;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "baseline", marginBottom: "4px" }}>
      {ruleId ? (
        <span
          className="text-dim"
          style={{ fontSize: "10px", fontFamily: "ui-monospace, monospace", letterSpacing: "0.02em" }}
        >
          {ruleId}
        </span>
      ) : null}
      <div style={{ fontWeight: 500 }}>{finding?.title}</div>
    </div>
  );
}

function PostureGapsPanel({ gaps, absentIntro, deferredIntro, effectiveFrameworks }) {
  const { absent, deferred } = gaps || { absent: [], deferred: [] };
  return (
    <section style={{ marginBottom: "18px" }}>
      <p style={{ fontSize: "11px", fontWeight: 600, marginBottom: "10px", color: "var(--text-secondary)" }}>
        <span style={{ color: "#94a3b8", marginRight: "6px" }} aria-hidden>
          ○
        </span>
        Posture gaps (not assessed)
      </p>
      {absent.length > 0 ? (
        <div style={{ marginBottom: deferred.length > 0 ? "12px" : 0 }}>
          <p className="text-dim" style={{ fontSize: "12px", lineHeight: 1.45, marginBottom: "8px" }}>
            {absentIntro || "Surface not configured — nothing to evaluate yet."}
          </p>
          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "8px" }}>
            {absent.map((f) => (
              <PostureGapRow key={f.id || f.rule_id} finding={f} effectiveFrameworks={effectiveFrameworks} />
            ))}
          </ul>
        </div>
      ) : null}
      {deferred.length > 0 ? (
        <div>
          <p className="text-dim" style={{ fontSize: "12px", lineHeight: 1.45, marginBottom: "8px" }}>
            {deferredIntro || "Requires workspace-level configuration — deferred to v2."}
          </p>
          <ul style={{ listStyle: "none", display: "flex", flexDirection: "column", gap: "8px" }}>
            {deferred.map((f) => (
              <PostureGapRow key={f.id || f.rule_id} finding={f} effectiveFrameworks={effectiveFrameworks} />
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function PostureGapRow({ finding, effectiveFrameworks }) {
  const ruleId = finding?.rule_id || finding?.ruleId;
  const frameworks = Array.isArray(finding?.frameworks) ? finding.frameworks : [];
  const eff = new Set(Array.isArray(effectiveFrameworks) ? effectiveFrameworks : []);
  const tags = frameworks
    .map((r) => String(r?.framework || "").trim())
    .filter((fw) => fw && eff.has(fw));
  const uniqueTags = [...new Set(tags)];

  return (
    <li
      style={{
        borderLeft: "3px solid #64748b",
        padding: "10px 12px",
        background: "rgba(100, 116, 139, 0.1)",
        borderRadius: "4px",
      }}
    >
      <FindingHeading finding={{ ...finding, ruleId, title: finding?.title }} />
      <p className="text-secondary" style={{ fontSize: "13px", lineHeight: 1.45 }}>
        {finding?.description || "Not applicable for this project state."}
      </p>
      {uniqueTags.length > 0 ? (
        <div style={{ marginTop: "6px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {uniqueTags.map((fw) => (
            <Badge key={fw} variant="archived">
              {fw}
            </Badge>
          ))}
        </div>
      ) : null}
    </li>
  );
}

function CompliancePosture({ findings, selectedFrameworks, effectiveFrameworks, providerId = "okta", onClickFramework }) {
  const selected = Array.isArray(selectedFrameworks)
    ? selectedFrameworks
    : getDefaultProviderComplianceFrameworks(providerId);
  const effective = Array.isArray(effectiveFrameworks) ? effectiveFrameworks : selected;
  const effSet = new Set(effective);

  const buckets = {
    fail: [
      ...(Array.isArray(findings?.critical) ? findings.critical : []),
      ...(Array.isArray(findings?.warnings) ? findings.warnings : []),
      ...(Array.isArray(findings?.other) ? findings.other : []),
    ],
    pass: Array.isArray(findings?.healthy) ? findings.healthy : [],
  };

  /** @type {Map<string, { covered: number, partial: number, failing: number }>} */
  const counts = new Map();
  for (const fw of effective) counts.set(String(fw), { covered: 0, partial: 0, failing: 0 });

  /** @type {Map<string, { hasFailPrimary: boolean, hasPass: boolean, hasAny: boolean }>} */
  const controlState = new Map();
  const consider = (finding, kind) => {
    const refs = Array.isArray(finding?.frameworks) ? finding.frameworks : [];
    for (const r of refs) {
      const fw = String(r?.framework || "");
      const ctrl = String(r?.control || "");
      if (!fw || !ctrl) continue;
      if (!effSet.has(fw)) continue;
      const key = `${fw}::${ctrl}`;
      const st = controlState.get(key) || { hasFailPrimary: false, hasPass: false, hasAny: false };
      st.hasAny = true;
      if (kind === "pass") st.hasPass = true;
      if (kind === "fail" && String(r?.relevance) === "primary") st.hasFailPrimary = true;
      controlState.set(key, st);
    }
  };

  for (const f of buckets.pass) consider(f, "pass");
  for (const f of buckets.fail) consider(f, "fail");

  for (const [key, st] of controlState.entries()) {
    const [fw] = key.split("::");
    const c = counts.get(fw);
    if (!c) continue;
    if (st.hasFailPrimary) c.failing += 1;
    else if (st.hasPass) c.covered += 1;
    else c.partial += 1;
  }

  const scopeLabel = `Scope: ${formatComplianceScopeDisplay(selected, providerId)}`;

  return (
    <section
      style={{
        marginBottom: "18px",
        padding: "14px 14px",
        borderRadius: "6px",
        border: "1px solid var(--border)",
        background: "var(--bg-surface)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "10px" }}>
        <div>
          <div className="text-dim" style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em" }}>
            COMPLIANCE POSTURE
          </div>
          <div className="text-secondary" style={{ fontSize: "12px", marginTop: "6px" }}>
            {scopeLabel}
          </div>
        </div>
        <div className="text-dim" style={{ fontSize: "11px" }}>
          Click a framework to focus
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px", marginTop: "12px" }}>
        {effective.map((fw) => {
          const def = FRAMEWORKS?.[fw];
          const name = def?.displayName || def?.name || fw;
          const c = counts.get(String(fw)) || { covered: 0, partial: 0, failing: 0 };
          return (
            <button
              key={fw}
              type="button"
              onClick={() => onClickFramework?.(String(fw))}
              style={{
                textAlign: "left",
                borderRadius: "6px",
                border: "1px solid var(--border)",
                background: "var(--bg-elevated)",
                padding: "10px 12px",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>{name}</div>
              <div className="text-secondary" style={{ fontSize: "12px", marginTop: "6px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <span>
                  <span style={{ color: "var(--healthy)" }}>✓</span> {c.covered} covered
                </span>
                <span>
                  <span style={{ color: "var(--warning)" }}>⚠</span> {c.partial} partial
                </span>
                <span>
                  <span style={{ color: "var(--critical)" }}>✗</span> {c.failing} failing
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function Citations({ finding, effectiveFrameworks }) {
  const eff = new Set(Array.isArray(effectiveFrameworks) ? effectiveFrameworks : []);
  const refs = Array.isArray(finding?.frameworks) ? finding.frameworks : [];
  const scoped = refs.filter((r) => r && eff.has(String(r.framework)));
  if (!scoped.length) return null;

  const primary = scoped.filter((r) => String(r.relevance) === "primary");
  const supporting = scoped.filter((r) => String(r.relevance) === "supporting");

  return (
    <div style={{ marginTop: "8px", fontSize: "11px", lineHeight: 1.4 }}>
      {primary.length ? (
        <div className="text-secondary">
          <span className="text-dim">Required by:</span> <CitationList refs={primary} />
        </div>
      ) : null}
      {supporting.length ? (
        <div className="text-dim" style={{ marginTop: primary.length ? "2px" : 0 }}>
          <span className="text-dim">Supports:</span> <CitationList refs={supporting} muted />
        </div>
      ) : null}
    </div>
  );
}

function CitationList({ refs, muted }) {
  const list = Array.isArray(refs) ? refs : [];
  return (
    <>
      {list.map((r, idx) => {
        const fw = String(r.framework || "");
        const ctrl = String(r.control || "");
        const def = FRAMEWORKS?.[fw];
        const c = def?.controls?.[ctrl];
        const label = `${def?.displayName || def?.name || fw} ${ctrl ? `§${ctrl}` : ""}`.trim();
        const href = c?.url || def?.url || null;
        return (
          <span key={`${fw}:${ctrl}:${idx}`}>
            {idx ? <span style={{ opacity: 0.6 }}> · </span> : null}
            {href ? (
              <a
                href={href}
                target="_blank"
                rel="noreferrer"
                style={{ color: muted ? "var(--text-dim)" : "var(--accent)", textDecoration: "none" }}
              >
                {label}
              </a>
            ) : (
              <span style={{ color: muted ? "var(--text-dim)" : "var(--accent)" }}>{label}</span>
            )}
          </span>
        );
      })}
    </>
  );
}
